import { useEditor } from "@/utils/useEditor"
import { useTranslation } from "react-i18next"
import { useAtomValue } from "jotai"
import { Canvas } from "@/components/Canvas"
import { Spinner } from "./ui/Spinner"
import { CanvasCropOverlay } from "./Crop/CanvasCropOverlay"
import { Icon } from "./icons/Icon"
import { isPhotoFullscreenAtom, showingCropPanelAtom } from "@/store/ui-views-state"
import { photosViewAtom } from "@/store/photo/photos-view-state"
import { currentPhotoAtom } from "@/store/photo/photos-state"
import { ReactNode, useEffect } from "react"
import { FloatingMasksPanel } from "@/components/Masking/FloatingMasksPanel"
import { MaskingToolsOverlay } from "@/components/Masking/MaskingToolsOverlay"
import { PeopleFaceBoxesOverlay } from "@/components/People/PeopleFaceBoxesOverlay"
import { AIModelLoadingStateAtom } from "@/store/app-state"
import toast from "react-hot-toast"
import { ToastView } from "./Project"
import { ColorPickerOverlay } from "./ColorPicker/ColorPickerOverlay"
import clsx from "clsx"
import { HealingToolsOverlay } from "@/components/Healing/HealingToolsOverlay"
import { showPhotoError, showUnsupportedError } from "@/store/photo/photo-error"
import { isCullingModeAtom, isManualModeAtom } from "@/store/project/projects-state"
import { getCullingTag, isSuggestingKeepersOrRejectsAtom } from "@/store/importing/culling-state"
import { editorBackgroundAtom } from "@/store/settings-state"
import { photoLoadingErrorAtom } from "@/store/editor/photo-loading"
import { IconName } from "./icons/icons"
import { PhotoRatingView } from "./PhotoRatingView"
import { CullingTagIcon, CullingTagSetter } from "./CullingTagSetter"
import { cullingTagKeys } from "@/i18n/dual-role-strings"

export const Editor = () => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const previewState = useAtomValue(photosViewAtom)
	const isShowingCrop = useAtomValue(showingCropPanelAtom)
	const currentPhoto = useAtomValue(currentPhotoAtom)
	const isManualMode = useAtomValue(isManualModeAtom)
	const isCullingMode = useAtomValue(isCullingModeAtom)
	const isShowingKeepersOrRejects = useAtomValue(isSuggestingKeepersOrRejectsAtom)
	const modelLoadingState = useAtomValue(AIModelLoadingStateAtom)
	const backgroundColor = useAtomValue(editorBackgroundAtom)
	const photoLoadingError = useAtomValue(photoLoadingErrorAtom)
	const isPhotoFullscreen = useAtomValue(isPhotoFullscreenAtom)
	const shouldShowCanvasOverlays = !isPhotoFullscreen

	useEffect(() => {
		if (modelLoadingState.model && modelLoadingState.progress) {
			toast.custom(
				<ToastView
					content={`Loading AI Models: ${modelLoadingState.model}... ${(
						modelLoadingState.progress * 100
					).toFixed(0)}%`}
				/>,
				{
					id: "model-loading",
					duration: Infinity,
				},
			)
		} else {
			toast.remove("model-loading")
		}

		return () => {
			toast.remove("model-loading")
		}
	}, [modelLoadingState])

	const renderStatus = () => {
		if (!currentPhoto) {
			return <></>
		}

		if (currentPhoto.isHidden) {
			return <StatusBadge icon="hidden" label={t("editor.badgeHidden")} />
		}

		if (currentPhoto.isUnsupported) {
			return (
				<StatusBadge
					icon="unsupported"
					label={t("editor.badgeUnsupported")}
					iconFill="fill-yellow-500"
					onClick={showUnsupportedError}
				/>
			)
		}

		if (photoLoadingError) {
			return (
				<StatusBadge
					icon="error"
					label={t("editor.badgeError")}
					iconFill="fill-yellow-500"
					onClick={() => showPhotoError(photoLoadingError)}
				/>
			)
		}

		if (isCullingMode && isShowingKeepersOrRejects) {
			const cullingTag = getCullingTag(currentPhoto)
			if (!cullingTag) return <></>

			return (
				<CullingTagSetter
					photo={currentPhoto}
					tag={cullingTag}
					size={14}
					contentSide="bottom"
					contentAlign="end"
					trackingLabel="focus_culling_tag_setter"
					triggerClassName="absolute right-2 top-2 z-10 cursor-pointer rounded-sm border-0 bg-base-900/80 p-0 text-base-300 hover:text-base-100"
				>
					<StatusBadgeContent
						iconNode={<CullingTagIcon tag={cullingTag} size={14} />}
						label={t(cullingTagKeys[cullingTag])}
					/>
				</CullingTagSetter>
			)
		}
		if (!isCullingMode) {
			switch (currentPhoto.status) {
				case "error":
					return (
						<StatusBadge
							icon="error"
							label={t("editor.badgeError")}
							iconFill="fill-yellow-500"
							onClick={() => {
								if (currentPhoto.error) {
									showPhotoError(currentPhoto.error)
								}
							}}
						/>
					)
				case "pending":
				case "applying":
					return <StatusBadge label={t("editor.badgeProcessing")} isLoading />
				case "none":
					if (!isManualMode) {
						switch (currentPhoto.kind) {
							case "reference":
								return (
									<StatusBadge
										icon="diamond"
										label={t("editor.badgeReference")}
									/>
								)
							case "locked":
								return <StatusBadge icon="locked" label={t("editor.badgeLocked")} />
							case "default":
								if (currentPhoto.styleId) {
									return (
										<StatusBadge
											icon="check_circle"
											label={t("editor.badgeProcessed")}
											iconFill="fill-green-500"
										/>
									)
								} else if (currentPhoto.isAnchorImage) {
									return (
										<StatusBadge
											icon="suggested"
											label={t("editor.badgeSuggested")}
											iconFill="fill-accent-600"
										/>
									)
								}
						}
					}
			}
		}
	}

	if (!editor) {
		return (
			<div className="relative flex size-full items-center justify-center overflow-hidden text-lg font-bold">
				{t("editor.notSupported")}
			</div>
		)
	}

	return (
		<div
			role="region"
			aria-label={t("editor.ariaEditorViewport")}
			aria-busy={currentPhoto?.status === "pending" || currentPhoto?.status === "applying"}
			className={clsx("relative flex flex-1")}
			style={{ background: backgroundColor }}
		>
			{shouldShowCanvasOverlays && previewState === "before/after" && (
				<div>
					<div className="absolute left-0 top-0 z-10 m-4 text-md">
						{t("editor.original")}
					</div>
					<div className="absolute left-1/2 top-0 z-10 m-4 text-md">
						{t("editor.after")}
					</div>
				</div>
			)}

			{shouldShowCanvasOverlays && renderStatus()}

			{shouldShowCanvasOverlays && <PhotoRatingView />}

			<div className={clsx(!currentPhoto && "invisible", "flex flex-1")}>
				<Canvas>
					{shouldShowCanvasOverlays && (
						<>
							{previewState === "focus" && isShowingCrop && <CanvasCropOverlay />}
							<FloatingMasksPanel />
							<PeopleFaceBoxesOverlay />
							<MaskingToolsOverlay />
							<HealingToolsOverlay />
							<ColorPickerOverlay />
						</>
					)}
				</Canvas>
			</div>
		</div>
	)
}

const StatusBadge = (props: {
	icon?: IconName
	iconNode?: ReactNode
	label: string
	iconFill?: string
	onClick?: () => void
	isLoading?: boolean
}) => {
	const { t } = useTranslation("components")
	return (
		<div
			role="status"
			aria-live="polite"
			aria-label={t("editor.photoStatus", { label: props.label })}
			className="absolute right-2 top-2 z-10 rounded-sm bg-base-900/80"
			onClick={props.onClick}
		>
			<StatusBadgeContent {...props} />
		</div>
	)
}

const StatusBadgeContent = (props: {
	icon?: IconName
	iconNode?: ReactNode
	label: string
	iconFill?: string
	isLoading?: boolean
}) => {
	return (
		<div className="m-1.5 flex flex-row items-center space-x-1">
			{props.isLoading ? (
				<Spinner size={14} />
			) : (
				<>
					{props.iconNode}
					{!props.iconNode && props.icon && (
						<Icon name={props.icon} size={14} className={props.iconFill} />
					)}
				</>
			)}
			<div className="text-sm">{props.label}</div>
		</div>
	)
}
